/**
 * 📡 TRANSMISSION DU COMMANDANT - Message holographique spatial
 * Style transmission radio / bulletin de bord du vaisseau
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Radio, Signal, Wifi } from 'lucide-react';

const FamilyMessage = ({ isDarkMode }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="max-w-4xl mx-auto mt-8 mb-6"
    >
      <div 
        className="relative overflow-hidden rounded-2xl"
        style={{
          background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #14532d 100%)',
          boxShadow: '0 0 50px rgba(34, 197, 94, 0.25), 0 20px 40px rgba(0,0,0,0.4)',
          border: '1px solid rgba(34, 197, 94, 0.4)'
        }}
      >
        {/* Étoiles animées */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 rounded-full bg-green-400"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                opacity: Math.random() * 0.3 + 0.1
              }}
              animate={{ opacity: [0.1, 0.5, 0.1], scale: [1, 1.3, 1] }}
              transition={{ duration: 2 + Math.random() * 2, repeat: Infinity, delay: Math.random() * 2 }}
            />
          ))}
        </div>

        {/* Scan line effect */}
        <motion.div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'linear-gradient(transparent 0%, rgba(34, 197, 94, 0.03) 50%, transparent 100%)',
            backgroundSize: '100% 4px'
          }}
          animate={{ backgroundPosition: ['0% 0%', '0% 100%'] }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
        />

        {/* Bordure supérieure néon */}
        <motion.div 
          className="absolute top-0 left-0 w-full h-1"
          style={{ background: 'linear-gradient(90deg, transparent, #22c55e, transparent)' }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        
        {/* HUD Corners */}
        <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-green-500" />
        <div className="absolute top-2 right-2 w-4 h-4 border-t-2 border-r-2 border-green-500" />
        <div className="absolute bottom-2 left-2 w-4 h-4 border-b-2 border-l-2 border-green-500/50" />
        <div className="absolute bottom-2 right-2 w-4 h-4 border-b-2 border-r-2 border-green-500/50" />

        {/* Header */}
        <div className="relative px-6 py-3 border-b border-green-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ opacity: [1, 0.5, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
              >
                <Radio className="w-4 h-4 text-green-500" />
              </motion.div>
              <span className="text-[10px] font-mono text-green-400 tracking-wider">TRANSMISSION PRIORITAIRE</span>
            </div>
            <div className="flex items-center gap-2">
              <motion.div
                className="flex items-center gap-1"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <Signal className="w-3 h-3 text-green-500" />
                <span className="text-[10px] font-mono text-green-400">SIGNAL FORT</span>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="relative p-8 text-center">
          {/* Hologram effect emojis */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {['💜', '✨', '🚀', '💫', '🌟', '💝'].map((emoji, i) => (
              <motion.div
                key={i}
                className="absolute text-2xl"
                style={{ left: `${15 + i * 14}%`, top: `${20 + (i % 3) * 25}%` }}
                animate={{
                  y: [0, -10, 0],
                  opacity: [0.2, 0.5, 0.2],
                  scale: [1, 1.2, 1]
                }}
                transition={{ duration: 3, delay: i * 0.4, repeat: Infinity, ease: "easeInOut" }}
              >
                {emoji}
              </motion.div>
            ))}
          </div>

          {/* Main holographic message */}
          <motion.div
            className="relative z-10"
            animate={{ 
              textShadow: [
                '0 0 20px rgba(34, 197, 94, 0.5)',
                '0 0 40px rgba(34, 197, 94, 0.8)',
                '0 0 20px rgba(34, 197, 94, 0.5)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.p 
              className="text-2xl sm:text-3xl font-black text-white leading-relaxed mb-6"
              style={{ textShadow: '0 0 30px rgba(34, 197, 94, 0.5)' }}
            >
              <span className="text-4xl">💜</span> Ici, t'es pas un client, t'es de la{' '}
              <motion.span 
                className="inline-block"
                style={{
                  background: 'linear-gradient(90deg, #22c55e, #06b6d4, #a855f7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
                animate={{ 
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                famille
              </motion.span> !
            </motion.p>
          </motion.div>
          
          {/* Bulletin items style spatial */}
          <div className="relative z-10 space-y-3 max-w-xl mx-auto">
            {[
              { emoji: '🎁', text: 'Tampons, cadeaux, surprises...', highlight: 'tout est gratuit !', color: '#ec4899' },
              { emoji: '🎮', text: 'Joue, gratte, tourne la roue...', highlight: 'et gagne des trucs de ouf !', color: '#06b6d4' },
              { emoji: '🚀', text: 'Livraison gratos le week-end,', highlight: 'parce que t\'es un VIP !', color: '#f59e0b' }
            ].map((item, i) => (
              <motion.div
                key={i}
                className="flex items-center justify-center gap-3 p-3 rounded-xl"
                style={{ 
                  background: 'rgba(0,0,0,0.3)',
                  border: `1px solid ${item.color}30`
                }}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + i * 0.1 }}
              >
                <span className="text-2xl">{item.emoji}</span>
                <p className="text-base sm:text-lg text-gray-200">
                  {item.text} <span className="font-bold" style={{ color: item.color }}>{item.highlight}</span>
                </p>
              </motion.div>
            ))}
          </div>

          {/* Bottom badge */}
          <motion.div 
            className="mt-8 inline-flex items-center gap-3 px-6 py-3 rounded-xl relative z-10"
            style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(6, 182, 212, 0.2) 100%)',
              border: '2px solid rgba(34, 197, 94, 0.4)',
              boxShadow: '0 0 30px rgba(34, 197, 94, 0.3)'
            }}
            animate={{ 
              boxShadow: [
                '0 0 20px rgba(34, 197, 94, 0.3)',
                '0 0 40px rgba(34, 197, 94, 0.5)',
                '0 0 20px rgba(34, 197, 94, 0.3)'
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <motion.span 
              className="text-2xl"
              animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🏆
            </motion.span>
            <span className="font-black text-lg text-white">
              Merci d'être là, on t'aime !
            </span>
            <motion.span 
              className="text-2xl"
              animate={{ rotate: [0, -10, 10, 0], scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
            >
              🏆
            </motion.span>
          </motion.div>
        </div>

        {/* Footer HUD */}
        <div className="px-6 py-2 border-t border-green-500/10 flex items-center justify-between">
          <span className="text-[10px] font-mono text-gray-600">📡 FAMILY BROADCAST v2.0</span>
          <div className="flex items-center gap-1">
            <motion.div className="w-1.5 h-1.5 rounded-full bg-green-500" animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1, repeat: Infinity }} />
            <div className="w-1.5 h-1.5 rounded-full bg-green-400/50" />
            <div className="w-1.5 h-1.5 rounded-full bg-green-300/30" />
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FamilyMessage;
